import React, { useState } from "react";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";

/**
 * @typedef {Object} dialogConfig
 * @prop {boolean} [noTitle] - remove the title
 * @prop {import("@mui/material").DialogProps["maxWidth"]} [maxWidth]
 * @prop {string} positiveActionText - provide to show the positive action button
 * @prop {boolean} positiveActionDisabled - override default
 * @prop {boolean} positiveActionLoading - override default
 * @prop {string} negativeActionText - provide to show the negative action button
 * @prop {boolean} negativeActionDisabled - override default
 * @prop {boolean} negativeActionLoading - override default 
 */

/**
 * @typedef {Object} contentParam
 * @prop {boolean} loading
 * @prop {boolean} disabled
 */

/**
 * Custom hook to faciliate dialog management
 * 
 * @param {object} props
 * @param {string?} props.title
 * @param {import("react").ReactNode} props.content - static content
 * @param {(params: contentParam) => import("react").ReactNode} props.getContent - dynamic content
 * @param {(any) => Promise<boolean>} [props.onPositiveAction] - return true to close dialog
 * @param {(any) => Promise<boolean>} [props.onNegativeAction] - return true to close dialog
 * @param {(any) => Promise<boolean>} [props.onOpen]
 * @param {(any) => Promise<boolean>} [props.onClose]
 * @param {dialogConfig} [props.config]
 * @returns 
 */
const useDialogManager = ({
  title,
  content,
  getContent,
  onPositiveAction,
  onNegativeAction,
  onOpen,
  onClose,
  config = {},
}) => {
  const [open, setOpen] = useState(false);
  const [positiveDisabled, setPositiveDisabled] = useState(false);
  const [positiveLoading, setPositiveLoading] = useState(false);
  const [negativeDisabled, setNegativeDisabled] = useState(false);
  const [negativeLoading, setNegativeLoading] = useState(false);

  const confirmFn = async (...args) => {
    if (onPositiveAction) {
      setNegativeDisabled(true);
      setPositiveLoading(true);
      const isSuccess = await onPositiveAction(...args);
      setNegativeDisabled(false);
      setPositiveLoading(false);
      if (isSuccess) {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  }

  const cancelFn = async (...args) => {
    if (onNegativeAction) {
      setPositiveDisabled(true);
      setNegativeLoading(true);
      const isSuccess = await onNegativeAction(...args);
      if (isSuccess) {
        setOpen(false);
      }
      setPositiveDisabled(false);
      setNegativeLoading(false);
    } else {
      setOpen(false);
    }
  }

  const dialogOncloseFn = async (...args) => {
    if (onClose) {
      const isSuccess = await onClose(...args);
      if (isSuccess) {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  }

  const openDialog = async (...args) => {
    if (onOpen) {
      const isSuccess = await onOpen(...args);
      if (isSuccess) {
        setOpen(true);
      }
    }
    setOpen(true);
  }

  const closeDialog = async (...args) => {
    if (onClose) {
      const isSuccess = await onClose(...args);
      if (isSuccess) {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  }

  const dialog = <SimpleDialog
    open={open}
    dialogOncloseFn={dialogOncloseFn}
    title={title}
    noTitle={config.noTitle}
    content={getContent ?
      getContent({ loading: positiveLoading || negativeLoading, disabled: positiveDisabled || negativeDisabled })
      : content}
    confirmFn={confirmFn}
    cancelFn={cancelFn}
    negativeAction={config.negativeActionText}
    negativeActionDisabled={config.negativeActionDisabled ?? negativeDisabled}
    negativeActionLoading={config.negativeActionLoading ?? negativeLoading}
    positiveAction={config.positiveActionText}
    positiveActionDisabled={config.positiveActionDisabled ?? positiveDisabled}
    positiveActionLoading={config.positiveActionLoading ?? positiveLoading}
    maxWidth={config.maxWidth}
  />

  return {
    dialog: dialog,
    open: open,
    setOpen: setOpen,
    openDialog: openDialog,
    closeDialog: closeDialog
  }
}

export default useDialogManager